import { Combobox as BaseUiCombobox } from "@base-ui/react/combobox"
import React from "react"
import { useTranslation } from "react-i18next"

import { NO_PROJECT_COLOR, Project } from "shared/types/Project"
import { SelectOption } from "shared/types/Select"
import { Button, ButtonProps } from "shared/ui/buttons/Button"
import { SplitButton } from "shared/ui/buttons/split-button/SplitButton"
import { Combobox } from "shared/ui/input/combobox/Combobox"
import { Skeleton } from "shared/ui/skeleton/Skeleton"

import { ProjectOption } from "entities/projects/ui/project-option/ProjectOption"
import { ProjectOptionNew } from "entities/projects/ui/project-option/ProjectOptionNew"

import { ActiveProjectSelectViewModel } from "./hooks/useActiveProjectSelectViewModel"
import "./styles/_active-project-select.scss"

function SecondaryTrigger(buttonProps: ButtonProps) {
  const { t } = useTranslation()
  return (
    <BaseUiCombobox.Trigger
      render={<Button {...buttonProps} />}
      aria-label={t("Project.ChangeActiveProject")}
      data-testid="active-project-select__open-select"
    />
  )
}

export const ActiveProjectSelectView = React.memo(
  ({
    activeProject,
    activeProjectHref,
    isLoading,

    title,
    color,

    items,
    selectedOption,

    splitButtonRef,

    tabIndex,

    onChange,
    onInputValueChange,
    onNavigateToProject
  }: ActiveProjectSelectViewModel) => {
    const { t } = useTranslation()

    if (isLoading) {
      return (
        <Skeleton fill containerClassName="active-project-select loading" />
      )
    }

    return (
      <Combobox.ControlBase
        value={selectedOption}
        onValueChange={onChange}
        items={items}
        onInputValueChange={onInputValueChange}
        isItemEqualToValue={(a, b) => a.value === b.value}
      >
        <SplitButton
          containerRef={splitButtonRef}
          primaryButtonLabel={title}
          onPrimaryClick={onNavigateToProject}
          secondaryButtonLabel={t("Project.ChangeActiveProject")}
          secondaryButtonRender={SecondaryTrigger}
          className="active-project-select"
          primaryButtonProps={{
            href: activeProjectHref,
            disabled: !activeProject,
            "data-testid": "active-project-select__open-project"
          }}
          variant="project-select"
          isDisabled={isLoading}
          tabIndex={tabIndex}
          data-testid="active-project-select"
        >
          <ProjectOption title={title} color={color} />
        </SplitButton>
        <Combobox.Portal>
          <Combobox.Positioner
            anchor={splitButtonRef}
            sideOffset={8}
            className="active-project-select__positioner"
          >
            <Combobox.Popup
              data-testid="active-project-select__select"
              className="active-project-select__popup"
            >
              <Combobox.InputContainer>
                <Combobox.Input
                  placeholder={t("Search.SearchFor", {
                    subject: t("Project.Project")
                  })}
                />
              </Combobox.InputContainer>
              <Combobox.Empty />
              <Combobox.List>
                {(option: SelectOption<string, Project>) =>
                  option.creatable ? (
                    <Combobox.Item key={option.value} value={option}>
                      <Combobox.ItemContent>
                        <ProjectOptionNew title={option.label} />
                      </Combobox.ItemContent>
                    </Combobox.Item>
                  ) : (
                    <Combobox.Item key={option.value} value={option}>
                      <Combobox.ItemIndicator />
                      <Combobox.ItemContent>
                        <ProjectOption
                          title={option.label}
                          color={option.data?.ui?.color ?? NO_PROJECT_COLOR}
                          isTeamProject={!!option.data?.team?.id}
                        />
                      </Combobox.ItemContent>
                    </Combobox.Item>
                  )
                }
              </Combobox.List>
            </Combobox.Popup>
          </Combobox.Positioner>
        </Combobox.Portal>
      </Combobox.ControlBase>
    )
  }
)
